import { Component, OnInit } from '@angular/core';
import { LoggedUser, User } from "../../common/logged-user.service";
import { translate, TranslocoService } from "@ngneat/transloco";
import { UserService } from "../../services/user.service";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { CommonMessagesService } from "../../services/common-messages.service";
import tinycolor from "tinycolor2";

@Component({
  selector: 'app-user-settings',
  templateUrl: './application-settings.component.html',
  styleUrls: ['./application-settings.component.scss']
})
export class ApplicationSettingsComponent implements OnInit {

  user: User;
  appCode: string;

  previewOptions: any[];
  applyOptions: any[];
  previewOption: 'list' | 'form' | 'details';
  applyOption: 'current' | 'all';

  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  globalDefaults: any;
  restoreDefaults: any;
  icon: string | ArrayBuffer;

  constructor(private loggedUser: LoggedUser,
              private userService: UserService,
              private activatedRoute: ActivatedRoute,
              private router: Router,
              private commonMessagesService: CommonMessagesService,
              private translocoService: TranslocoService) { }

  ngOnInit(): void {
    this.globalDefaults = { primaryColor: '#407EC9', secondaryColor: '#706f6f', tertiaryColor: '#b9babb', icon: this.userService.getDefaultIcon() };
    this.loggedUser.userObservable.toPromise().then( user => this.user = user);
    this.buildStaticOptions();
    this.buildThemeOptions();
  }

  onPrimaryColorChange(color) {
    let primaryLight = tinycolor(color).lighten(25);
    document.documentElement.style.setProperty('--primary-color-preview', color);
    document.documentElement.style.setProperty('--primary-color-light-preview', primaryLight.toRgbString());
  }

  onSecondaryColorChange(color) {
    document.documentElement.style.setProperty('--secondary-color-preview', color);
  }

  onTertiaryColorChange(color) {
    document.documentElement.style.setProperty('--tertiary-color-preview', color);
  }

  onUploadIcon(input: any) {
    const file: File = input.files[0];
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => this.icon = reader.result;
  }

  onSaveChanges() {
    let newPreferences = {
      primaryColor: this.primaryColor,
      secondaryColor: this.secondaryColor,
      tertiaryColor: this.tertiaryColor,
      icon: this.icon
    };

    let observable;
    if(this.applyOption === 'current') {
      observable = this.userService.updateUserPreferences(this.appCode, newPreferences);
    } else if(this.applyOption === 'all') {
      observable = this.userService.updateAllAppUserPreferences(newPreferences);
    }

    observable.subscribe(() => {
      this.commonMessagesService.showChangesSavedMessage();
      this.router.navigate(["/"]);
    }, () => {
      this.commonMessagesService.showErrorWhileSavingChangesMessage();
    })
  }

  private buildStaticOptions() {
    this.translocoService.selectTranslate('preview.list').subscribe(() => {
      this.previewOptions = [
        {value: 'list', label: translate('preview.list')},
        {value: 'form', label: translate('preview.form')},
        {value: 'details', label: translate('preview.details')}
      ];
      this.previewOption = 'list';
      this.applyOptions = [
        {label: translate('options.currentApplication'), value: 'current'},
        {label: translate('options.allApplications'), value: 'all'}
      ];
      this.applyOption = 'current';
    });
  }

  private buildThemeOptions() {
    this.activatedRoute.paramMap.subscribe((params: ParamMap) => {
      this.appCode = params.get('appCode');
      if(this.appCode === 'CONTROL_PLATFORM') {
        this.router.navigate(['**'])
      }

      this.userService.getUserApplication(this.appCode).subscribe(userApplication => {
        try {
          let defaults = userApplication.defaultPreferences ? JSON.parse(userApplication.defaultPreferences) : {};
          let preferences = userApplication.preferences ? JSON.parse(userApplication.preferences) : {};

          this.primaryColor = preferences.primaryColor ?? defaults.primaryColor ?? this.globalDefaults.primaryColor;
          this.secondaryColor = preferences.secondaryColor ?? defaults.secondaryColor ?? this.globalDefaults.secondaryColor;
          this.tertiaryColor = preferences.tertiaryColor ?? defaults.tertiaryColor ?? this.globalDefaults.tertiaryColor;
          this.icon = preferences.icon ?? this.globalDefaults.icon;

          this.restoreDefaults = {
            primaryColor: defaults.primaryColor ?? this.globalDefaults.primaryColor,
            secondaryColor: defaults.secondaryColor ?? this.globalDefaults.secondaryColor,
            tertiaryColor: defaults.tertiaryColor ?? this.globalDefaults.tertiaryColor,
            icon: this.globalDefaults.icon
          };
        } catch(e) {
          this.primaryColor = this.globalDefaults.primaryColor;
          this.secondaryColor = this.globalDefaults.secondaryColor;
          this.tertiaryColor = this.globalDefaults.tertiaryColor;
          this.icon = this.globalDefaults.icon;

          this.restoreDefaults = {
            primaryColor: this.globalDefaults.primaryColor,
            secondaryColor: this.globalDefaults.secondaryColor,
            tertiaryColor: this.globalDefaults.tertiaryColor,
            icon: this.globalDefaults.icon
          };
        } finally {
          this.onPrimaryColorChange(this.primaryColor);
          this.onSecondaryColorChange(this.secondaryColor);
          this.onTertiaryColorChange(this.tertiaryColor);
        }
      }, () => {
        this.router.navigate(['**'])
      })
    });
  }

  restoreDefaultColors() {
    this.commonMessagesService.showRestoreDefaultsConfirmation(() => {
      this.primaryColor = this.restoreDefaults.primaryColor;
      this.secondaryColor = this.restoreDefaults.secondaryColor;
      this.tertiaryColor = this.restoreDefaults.tertiaryColor;
      this.onPrimaryColorChange(this.primaryColor);
      this.onSecondaryColorChange(this.secondaryColor);
      this.onTertiaryColorChange(this.tertiaryColor);
    });
  }

  restoreDefaultIcon() {
    this.commonMessagesService.showRestoreDefaultsConfirmation(() => {
      this.icon = this.restoreDefaults.icon;
    });
  }

}
